/**
 * NewConversationModal — pick an agent to start a thread inside a project.
 *
 * Mirrors the branch-to-agent chooser the user already knows (search + a grid
 * of agent tiles), with one addition that belongs only here: **the consent
 * step**.
 *
 * An agent that has never been used in this project carries its own
 * instructions, knowledge files and skills into a room that may contain people
 * from other organizations. Bringing it in is therefore a disclosure decision,
 * and it is asked at the moment it happens — once per agent per project, not on
 * every conversation, because a prompt that fires every time stops being read.
 * An agent already working here skips straight through.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BotAvatar } from '@/components/common/BotAvatar';
import { useTranslator } from '../hooks/useTranslator';
import botService from '@modules/chatbots/services/botService';
import type { Bot } from '@/types/models';
import type { ProjectAgent, ProjectMemberRow } from '../types';
import { SkeletonRows, ErrorBlock } from './StateBlocks';
import './NewConversationModal.css';

interface NewConversationModalProps {
  isOpen: boolean;
  /** Agents already used in this project — these skip the consent step. */
  projectAgents: ProjectAgent[];
  /** Accepted members, used to say WHO the agent would become reachable by. */
  members: ProjectMemberRow[];
  /** The project's own tenant, for deciding what counts as "outside". */
  projectTenantId: string;
  onClose: () => void;
  onStart: (bot: Bot, threadMode: 'group' | 'solo') => void;
}

export function NewConversationModal({
  isOpen,
  projectAgents,
  members,
  projectTenantId,
  onClose,
  onStart,
}: NewConversationModalProps) {
  const { t, tRef } = useTranslator();
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [pendingBot, setPendingBot] = useState<Bot | null>(null);
  /**
   * Who may write in the thread being started. Group is the default because a
   * project is a shared workspace and the shared case is the common one; solo
   * is the deliberate choice, so it is the one that has to be picked.
   */
  const [threadMode, setThreadMode] = useState<'group' | 'solo'>('group');
  const searchRef = useRef<HTMLInputElement>(null);

  const known = useMemo(
    () => new Set(projectAgents.map(a => a.chatbot_id)),
    [projectAgents],
  );

  /** Organizations other than the project's own that already have members here. */
  const outsideOrgs = useMemo(() => {
    const names = new Set<string>();
    members.forEach(member => {
      if (member.status !== 'accepted') return;
      if (member.is_tenant_grant) {
        if (member.member_tenant_id && member.member_tenant_id !== projectTenantId) {
          names.add(member.member_name || member.member_tenant_name || '');
        }
        return;
      }
      // Individual members carry their own tenant label; a name is only worth
      // showing when it differs from the project's own organization.
      if (member.member_tenant_name) names.add(member.member_tenant_name);
    });
    names.delete('');
    return Array.from(names);
  }, [members, projectTenantId]);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setPendingBot(null);
    setLoading(true);
    setError(null);
    let cancelled = false;
    botService
      .getBots()
      .then(data => { if (!cancelled) setBots(data || []); })
      .catch(() => {
        if (!cancelled) {
          setError(tRef.current('chat.agentBranch.loadFailed')
            || t('projects.newConversation.loadAgentsFailed'));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Only if the user hasn't already focused something — see ProjectFormModal.
    const id = window.setTimeout(() => {
      const active = document.activeElement;
      if (!active || active === document.body) searchRef.current?.focus();
    }, 60);
    return () => { cancelled = true; window.clearTimeout(id); };
    // `t` out of the deps on purpose — see useTranslator.ts.
  }, [isOpen, tRef]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (pendingBot) setPendingBot(null);
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, pendingBot]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return bots;
    return bots.filter(b =>
      (b.chatbot_name || '').toLowerCase().includes(q)
      || (b.chatbot_description || '').toLowerCase().includes(q));
  }, [bots, query]);

  if (!isOpen) return null;

  const choose = (bot: Bot) => {
    if (known.has(bot.chatbot_id)) {
      onStart(bot, threadMode);
      return;
    }
    setPendingBot(bot);
  };

  return createPortal(
    <div className="pj-overlay" onClick={onClose} role="presentation">
      <div
        className="pj-modal pj-modal-wide newconv-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('projects.newConversation.title') || 'Start a conversation'}
        onClick={e => e.stopPropagation()}
      >
        <header className="pj-modal-header">
          <div className="pj-modal-heading">
            <h2 className="pj-modal-title">
              {pendingBot
                ? (t('projects.newConversation.consentTitle') || 'Bring this agent into the project?')
                : (t('projects.newConversation.title') || 'Start a conversation')}
            </h2>
            <p className="pj-modal-subtitle">
              {pendingBot
                ? (t('projects.newConversation.consentSubtitle')
                  || t('projects.newConversation.agentNotUsedYet'))
                : (t('projects.newConversation.subtitle')
                  || t('projects.newConversation.agentReceivesBrief'))}
            </p>
          </div>
          <button
            type="button"
            className="pj-icon-btn"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        {pendingBot ? (
          <>
            <div className="pj-modal-body">
              <div className="newconv-consent-agent">
                <BotAvatar
                  name={pendingBot.chatbot_name || 'AI'}
                  size={40}
                  colorOverride={(pendingBot as any).avatar_color}
                  variantOverride={(pendingBot as any).avatar_variant ?? undefined}
                />
                <div>
                  <p className="newconv-consent-name" dir="auto">{pendingBot.chatbot_name}</p>
                  {pendingBot.chatbot_description && (
                    <p className="newconv-consent-desc" dir="auto">{pendingBot.chatbot_description}</p>
                  )}
                </div>
              </div>

              <div className="pj-notice pj-notice-warning">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
                </svg>
                <div className="newconv-consent-text">
                  <strong>
                    {t('projects.newConversation.consentWhat')
                      || 'Everyone in this project will be able to use this agent here.'}
                  </strong>
                  <p>
                    {t('projects.newConversation.consentDetail')
                      || 'That includes its instructions, its knowledge files and its skills. Make sure this agent is safe to share with the people below.'}
                  </p>
                  {outsideOrgs.length > 0 && (
                    <p className="newconv-consent-orgs">
                      {t('projects.newConversation.consentOrgs') || 'Organizations in this project:'}{' '}
                      <span>{outsideOrgs.join(', ')}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
            <footer className="pj-modal-footer">
              <button type="button" className="pj-btn pj-btn-secondary" onClick={() => setPendingBot(null)}>
                {t('common.back') || 'Back'}
              </button>
              <button
                type="button"
                className="pj-btn pj-btn-primary"
                onClick={() => onStart(pendingBot, threadMode)}
              >
                {t('projects.newConversation.consentConfirm') || 'Use this agent'}
              </button>
            </footer>
          </>
        ) : (
          <div className="pj-modal-body">
            {/*
              Who may write, chosen BEFORE the agent — it changes what the
              thread IS, not how it is configured afterwards. Both options stay
              visible rather than hiding behind a toggle: someone who does not
              know solo threads exist will never go looking for one, and the
              whole difference between them is a sentence.
            */}
            <div
              className="newconv-mode"
              role="radiogroup"
              aria-label={t('projects.newConversation.modeLabel') || 'Who can write in this thread'}
            >
              {(['group', 'solo'] as const).map(mode => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={threadMode === mode}
                  className={`newconv-mode-option ${threadMode === mode ? 'is-selected' : ''}`}
                  onClick={() => setThreadMode(mode)}
                >
                  <span className="newconv-mode-icon" aria-hidden="true">
                    {mode === 'group' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="9" cy="8" r="3.2" />
                        <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
                        <path d="M16 5.4a3.2 3.2 0 0 1 0 5.2M17.5 14.2A6.5 6.5 0 0 1 21.5 20" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="3.4" />
                        <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
                      </svg>
                    )}
                  </span>
                  <span className="newconv-mode-text">
                    <span className="newconv-mode-title">
                      {mode === 'group'
                        ? (t('projects.newConversation.modeGroupTitle') || 'Anyone can join in')
                        : (t('projects.newConversation.modeSoloTitle') || 'Only I can write')}
                    </span>
                    <span className="newconv-mode-hint">
                      {mode === 'group'
                        ? (t('projects.newConversation.modeGroupHint')
                          || t('projects.newConversation.everyoneCanReply'))
                        : (t('projects.newConversation.modeSoloHint')
                          || t('projects.newConversation.onlyYouCanSend'))}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            <div className="share-search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                ref={searchRef}
                dir="auto"
                className="share-search-input"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('chat.agentBranch.searchPlaceholder') || 'Search agents…'}
                aria-label={t('chat.agentBranch.searchPlaceholder') || 'Search agents'}
              />
            </div>

            {loading ? (
              <SkeletonRows rows={3} />
            ) : error ? (
              <ErrorBlock message={error} />
            ) : filtered.length === 0 ? (
              <p className="newconv-none">
                {query
                  ? (t('chat.agentBranch.noResults') || 'No agents match your search.')
                  : (t('projects.newConversation.noAgents')
                    || t('projects.newConversation.noAgentsYet'))}
              </p>
            ) : (
              <div className="newconv-grid">
                {filtered.map(bot => (
                  <button
                    key={bot.chatbot_id}
                    type="button"
                    className="newconv-card"
                    onClick={() => choose(bot)}
                  >
                    <span className="newconv-card-head">
                      <BotAvatar
                        name={bot.chatbot_name || 'AI'}
                        size={32}
                        colorOverride={(bot as any).avatar_color}
                        variantOverride={(bot as any).avatar_variant ?? undefined}
                      />
                      <span className="newconv-card-name" dir="auto">{bot.chatbot_name}</span>
                      {known.has(bot.chatbot_id) && (
                        <span className="pj-pill pj-pill-accent">
                          {t('projects.newConversation.inProject') || 'In project'}
                        </span>
                      )}
                    </span>
                    <span className="newconv-card-desc bidi-clamp" dir="auto">
                      {bot.chatbot_description || (t('agents.noDescription') || 'No description')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default NewConversationModal;
